import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password, needs2fa ? code : undefined);
      toast.success(`Welcome, ${u.name}`);
      nav("/admin");
    } catch (err) {
      const msg = apiError(err);
      if (msg === "2FA_REQUIRED") {
        setNeeds2fa(true);
        toast.info("Enter the 6-digit code from your authenticator app");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 pad-safe">
      <button className="fixed top-5 left-5 btn btn-ghost h-10 px-4 text-sm" onClick={() => nav("/")} data-testid="back-home"><ArrowLeft size={16} /> Back</button>
      <div className="w-full max-w-md fadeup">
        <div className="text-center mb-8"><Wordmark className="inline-block" /></div>
        <div className="card p-8">
          <h1 className="font-serif text-3xl mb-1">Staff Sign In</h1>
          <p className="text-sm text-[color:var(--ink-2)] mb-6">Finance Admin, Coordinator &amp; Auditor.</p>
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="label block mb-1.5">Email</span>
              <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" autoComplete="username" />
            </label>
            <label className="block">
              <span className="label block mb-1.5">Password</span>
              <input className="field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" autoComplete="current-password" />
            </label>
            {needs2fa && (
              <div className="fadeup space-y-2">
                <label className="block">
                  <span className="label block mb-1.5">{useRecovery ? "Recovery Code" : "Authenticator Code"}</span>
                  {useRecovery ? (
                    <input className="field font-mono tracking-[0.15em]" value={code} onChange={(e) => setCode(e.target.value.trim())} placeholder="xxxxxxxxxx" data-testid="login-recovery-code" autoFocus />
                  ) : (
                    <input className="field font-mono tabnum tracking-[0.3em] text-center" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="login-2fa-code" autoFocus />
                  )}
                </label>
                <button type="button" className="text-xs text-[color:var(--olive)] underline" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }} data-testid="login-toggle-recovery">
                  {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
                </button>
              </div>
            )}
            <button className="btn btn-primary w-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : needs2fa ? "Verify & Sign In" : "Sign In"}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
